/**
 * Notification sound utility using Web Audio API and HTML5 Audio
 * Provides methods to play notification sounds for various events
 */

let audioContext = null
const audioCache = new Map()
const currentlyPlaying = new Set()

/**
 * Initialize audio context (lazy initialization)
 * @returns {AudioContext|null}
 */
function getAudioContext() {
  if (!audioContext) {
    try {
      audioContext = new (window.AudioContext || window.webkitAudioContext)()
    } catch (e) {
      console.warn('Web Audio API not supported:', e)
      return null
    }
  }
  return audioContext
}

/**
 * Check if a sound can be played (not currently playing)
 * @param {string} soundId - Unique identifier for the sound
 * @returns {boolean}
 */
function canPlaySound(soundId) {
  return !currentlyPlaying.has(soundId)
}

/**
 * Mark sound as playing
 * @param {string} soundId - Unique identifier for the sound
 * @param {number} duration - Duration of the sound in milliseconds
 */
function markSoundPlaying(soundId, duration) {
  currentlyPlaying.add(soundId)

  // Remove from playing set after duration
  setTimeout(() => {
    currentlyPlaying.delete(soundId)
  }, duration)
}

/**
 * Play a two-tone notification sound
 * Used for new unassigned events
 */
export function playUnassignedEventSound() {
  const soundId = 'unassigned-event'

  if (!canPlaySound(soundId)) {
    return
  }

  try {
    const ctx = getAudioContext()
    if (!ctx) return

    const duration = 300 // 0.3 seconds
    markSoundPlaying(soundId, duration)

    const now = ctx.currentTime

    // Create oscillator for the notification tone
    const oscillator = ctx.createOscillator()
    const gainNode = ctx.createGain()

    oscillator.connect(gainNode)
    gainNode.connect(ctx.destination)

    // Two-tone notification sound: A5 -> C6
    oscillator.type = 'sine'
    oscillator.frequency.setValueAtTime(880, now) // A5
    oscillator.frequency.setValueAtTime(1046.5, now + 0.1) // C6

    // Envelope for smooth sound
    gainNode.gain.setValueAtTime(0, now)
    gainNode.gain.linearRampToValueAtTime(0.3, now + 0.02)
    gainNode.gain.linearRampToValueAtTime(0.2, now + 0.1)
    gainNode.gain.linearRampToValueAtTime(0.3, now + 0.12)
    gainNode.gain.linearRampToValueAtTime(0, now + 0.3)

    oscillator.start(now)
    oscillator.stop(now + 0.3)
  } catch (e) {
    console.warn('Could not play notification sound:', e)
    currentlyPlaying.delete(soundId)
  }
}

/**
 * Play an alert sound for critical events
 * Can be used for patrol errors or critical tasks
 */
export function playAlertSound() {
  const soundId = 'alert'

  if (!canPlaySound(soundId)) {
    return
  }

  try {
    const ctx = getAudioContext()
    if (!ctx) return

    const duration = 500 // 0.5 seconds
    markSoundPlaying(soundId, duration)

    const now = ctx.currentTime

    const oscillator = ctx.createOscillator()
    const gainNode = ctx.createGain()

    oscillator.connect(gainNode)
    gainNode.connect(ctx.destination)

    // Urgent three-tone sound: E5 -> C5 -> E5
    oscillator.type = 'square'
    oscillator.frequency.setValueAtTime(659.25, now) // E5
    oscillator.frequency.setValueAtTime(523.25, now + 0.15) // C5
    oscillator.frequency.setValueAtTime(659.25, now + 0.3) // E5

    gainNode.gain.setValueAtTime(0, now)
    gainNode.gain.linearRampToValueAtTime(0.25, now + 0.02)
    gainNode.gain.linearRampToValueAtTime(0.2, now + 0.15)
    gainNode.gain.linearRampToValueAtTime(0.25, now + 0.17)
    gainNode.gain.linearRampToValueAtTime(0.2, now + 0.3)
    gainNode.gain.linearRampToValueAtTime(0.25, now + 0.32)
    gainNode.gain.linearRampToValueAtTime(0, now + 0.5)

    oscillator.start(now)
    oscillator.stop(now + 0.5)
  } catch (e) {
    console.warn('Could not play alert sound:', e)
    currentlyPlaying.delete(soundId)
  }
}

/**
 * Play a simple success/info sound
 * Used for completed tasks or general notifications
 */
export function playInfoSound() {
  const soundId = 'info'

  if (!canPlaySound(soundId)) {
    return
  }

  try {
    const ctx = getAudioContext()
    if (!ctx) return

    const duration = 200 // 0.2 seconds
    markSoundPlaying(soundId, duration)

    const now = ctx.currentTime

    const oscillator = ctx.createOscillator()
    const gainNode = ctx.createGain()

    oscillator.connect(gainNode)
    gainNode.connect(ctx.destination)

    // Single tone rising: C5 -> E5
    oscillator.type = 'sine'
    oscillator.frequency.setValueAtTime(523.25, now) // C5
    oscillator.frequency.exponentialRampToValueAtTime(659.25, now + 0.2) // E5

    gainNode.gain.setValueAtTime(0, now)
    gainNode.gain.linearRampToValueAtTime(0.2, now + 0.02)
    gainNode.gain.linearRampToValueAtTime(0, now + 0.2)

    oscillator.start(now)
    oscillator.stop(now + 0.2)
  } catch (e) {
    console.warn('Could not play info sound:', e)
    currentlyPlaying.delete(soundId)
  }
}
// alarm.mp3
// cancel.mp3
// completed.mp3
// event.mp3
// facility_alarm.mp3
// notification.mp3
// sci_fi_alarm.mp3
// security_alarm.mp3
// siren_alarm_long.mp3
// siren_alarm.mp3
// street_public_alarm.mp3
// warning_long.mp3
// warning.mp3

// Export play methods for each sound file as functions
export const playAlarm = (volume = 0.5, allowMultiple = false) => playAudioFile('/sounds/alarm.mp3', volume, allowMultiple)
export const playCancel = (volume = 0.5, allowMultiple = false) => playAudioFile('/sounds/cancel.mp3', volume, allowMultiple)
export const playCompleted = (volume = 0.5, allowMultiple = false) => playAudioFile('/sounds/completed.mp3', volume, allowMultiple)
export const playEvent = (volume = 0.5, allowMultiple = false) => playAudioFile('/sounds/event.mp3', volume, allowMultiple)
export const playFacilityAlarm = (volume = 0.5, allowMultiple = false) => playAudioFile('/sounds/facility_alarm.mp3', volume, allowMultiple)
export const playNotification = (volume = 0.5, allowMultiple = false) => playAudioFile('/sounds/notification.mp3', volume, allowMultiple)
export const playSciFiAlarm = (volume = 0.5, allowMultiple = false) => playAudioFile('/sounds/sci_fi_alarm.mp3', volume, allowMultiple)
export const playSecurityAlarm = (volume = 0.5, allowMultiple = false) => playAudioFile('/sounds/security_alarm.mp3', volume, allowMultiple)
export const playSirenAlarmLong = (volume = 0.5, allowMultiple = false) => playAudioFile('/sounds/siren_alarm_long.mp3', volume, allowMultiple)
export const playSirenAlarm = (volume = 0.5, allowMultiple = false) => playAudioFile('/sounds/siren_alarm.mp3', volume, allowMultiple)
export const playStreetPublicAlarm = (volume = 0.5, allowMultiple = false) => playAudioFile('/sounds/street_public_alarm.mp3', volume, allowMultiple)
export const playWarningLong = (volume = 0.5, allowMultiple = false) => playAudioFile('/sounds/warning_long.mp3', volume, allowMultiple)
export const playWarning = (volume = 0.5, allowMultiple = false) => playAudioFile('/sounds/warning.mp3', volume, allowMultiple)

/**
 * Play audio from a file
 * @param {string} audioPath - Path to the audio file (e.g., '/sounds/notification.mp3')
 * @param {number} volume - Volume level from 0.0 to 1.0 (default: 0.5)
 * @param {boolean} allowMultiple - Allow multiple instances of the same sound (default: false)
 * @returns {Promise<void>}
 */
export function playAudioFile(audioPath, volume = 0.5, allowMultiple = false) {
  const soundId = `file:${audioPath}`

  if (!allowMultiple && !canPlaySound(soundId)) {
    return Promise.resolve()
  }

  return new Promise((resolve, reject) => {
    try {
      // Check if audio is already cached
      let audio = audioCache.get(audioPath)

      if (!audio) {
        // Create new Audio instance
        audio = new Audio(audioPath)
        audio.preload = 'auto'
        audioCache.set(audioPath, audio)
      }

      // Clone the audio to allow multiple simultaneous plays
      const audioClone = audio.cloneNode()
      audioClone.volume = Math.max(0, Math.min(1, volume))

      // Duration will be known after loadedmetadata or use actual duration after ended
      let soundMarked = false

      const markSoundIfNeeded = () => {
        if (!soundMarked && !allowMultiple) {
          const duration = audioClone.duration && !isNaN(audioClone.duration)
            ? audioClone.duration * 1000
            : 3000 // fallback 3s
          markSoundPlaying(soundId, duration)
          soundMarked = true
        }
      }

      audioClone.addEventListener('loadedmetadata', () => {
        markSoundIfNeeded()
      })

      audioClone.addEventListener('ended', () => {
        if (!allowMultiple) {
          currentlyPlaying.delete(soundId)
        }
        resolve()
      })

      audioClone.addEventListener('error', (e) => {
        if (!allowMultiple) {
          currentlyPlaying.delete(soundId)
        }
        console.warn(`Could not play audio file ${audioPath}:`, e)
        reject(e)
      })

      audioClone.play().then(() => {
        markSoundIfNeeded()
      }).catch((err) => {
        if (!allowMultiple) {
          currentlyPlaying.delete(soundId)
        }
        reject(err)
      })
    } catch (e) {
      if (!allowMultiple) {
        currentlyPlaying.delete(soundId)
      }
      console.warn(`Could not play audio file ${audioPath}:`, e)
      reject(e)
    }
  })
}

/**
 * Preload audio file into cache
 * Useful for ensuring instant playback of important sounds
 * @param {string} audioPath - Path to the audio file
 * @returns {Promise<void>}
 */
export function preloadAudioFile(audioPath) {
  return new Promise((resolve, reject) => {
    try {
      if (audioCache.has(audioPath)) {
        resolve()
        return
      }

      const audio = new Audio(audioPath)
      audio.preload = 'auto'

      audio.addEventListener('canplaythrough', () => {
        audioCache.set(audioPath, audio)
        resolve()
      })

      audio.addEventListener('error', (e) => {
        console.warn(`Could not preload audio file ${audioPath}:`, e)
        reject(e)
      })
    } catch (e) {
      console.warn(`Could not preload audio file ${audioPath}:`, e)
      reject(e)
    }
  })
}

/**
 * Clear audio cache
 * Useful for memory management
 */
export function clearAudioCache() {
  audioCache.clear()
}
